import { memo, useContext, useCallback, useMemo, type FunctionComponent } from 'react';
import size from 'lodash/size';
import map from 'lodash/map';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
// Skillmore UI Components
import { type ExportFormat } from '@empathco/ui-components/src/models/exportFormat';
import { injectParams } from '@empathco/ui-components/src/helpers/path';
// local imports
import { Skill } from '../models/skill';
import { Employee } from '../models/employee';
import { getTbErrorMessage } from '../constants/teamBuilderErrors';
import { PATH_HR_TEAM_BUILDER, PATH_HR_TEAM_BUILDER_TEAM } from '../config/paths';
import useExportRoster from '../hooks/useExportRoster';
import { SupervisorContext } from '../context/supervisor';
import ReviewBar from '../elements/ReviewBar';

type TbReviewBarProps = {
  readonly teamId: number;
  readonly title?: string;
  readonly roster?: Employee[];
  readonly skills?: Skill[];
  readonly pending?: boolean;
  readonly disabled?: boolean;
}

const TbReviewBar: FunctionComponent<TbReviewBarProps> = ({
  teamId,
  title,
  roster,
  skills,
  pending = false,
  disabled: disabledParent = false
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { exportRoster, ROSTER_CSV_COLUMNS } = useExportRoster();

  const {
    tbTeamUpdate: { pending: renamePending, failed: renameFailed, params: renameParams }, tbUpdateTeam,
    tbTeamDelete: { pending: deletePending, failed: deleteFailed }, tbDeleteTeam
  } = useContext(SupervisorContext);

  const updateTitle = useCallback(
    (newTitle?: string) => tbUpdateTeam?.({ team_id: teamId, title: newTitle ? newTitle : '' }),
    [teamId, tbUpdateTeam]
  );

  const deleteTeam = useCallback(() => tbDeleteTeam?.({
    team_id: teamId,
    onSuccess: () => navigate(PATH_HR_TEAM_BUILDER)
  }), [tbDeleteTeam, teamId, navigate]);

  const link = useMemo(() => injectParams(PATH_HR_TEAM_BUILDER_TEAM, { team_id: teamId }), [teamId]);

  const renameError = useMemo(
    () => getTbErrorMessage('hr.teambuilder.review.rename_error', renameParams?.errorCode),
    [renameParams?.errorCode]
  );

  const getExport = useCallback((format: ExportFormat, teamTitle: string) => {
    if (!roster || !skills || !teamTitle) return null;
    return exportRoster(
      format,
      teamTitle,
      map(ROSTER_CSV_COLUMNS, (id) => formatMessage({ id })),
      skills,
      roster
    );
  }, [roster, skills, formatMessage, ROSTER_CSV_COLUMNS, exportRoster]);

  return (
    <ReviewBar
        titleLabel="hr.teambuilder.review.title_label"
        title={title}
        link={link}
        pending={pending}
        disabled={disabledParent}
        editLabel="hr.teambuilder.review.edit"
        onRename={updateTitle}
        renameLabel="hr.teambuilder.review.rename"
        renamePending={renamePending}
        renameFailed={renameFailed}
        renameError={renameError}
        onDelete={deleteTeam}
        deleteLabel="common.button.delete"
        deleteTitle="hr.teambuilder.review.delete_title"
        deleteQuestion="hr.teambuilder.review.delete_question"
        deletePending={deletePending}
        deleteFailed={deleteFailed}
        deleteError="hr.teambuilder.review.delete_error"
        onExport={getExport}
        exportDisabled={!skills || !roster || size(roster) < 1}
    />
  );
};

export default memo(TbReviewBar);
